import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useMemo, useRef, useState } from 'react';

interface PasteGridProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onDataChange?: (data: any[], columns: Record<string, string>) => void;
}

interface ParseResult {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any[];
    columns: ColDef[];
    mapping: Record<string, string>;
}

const PasteGrid: React.FC<PasteGridProps> = ({ onDataChange }) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [rowData, setRowData] = useState<any[]>([]);
    const [columnDefs, setColumnDefs] = useState<ColDef[]>([
        { field: 'col1', headerName: 'Column 1', editable: true },
        { field: 'col2', headerName: 'Column 2', editable: true },
        { field: 'col3', headerName: 'Column 3', editable: true },
    ]);
    const gridRef = useRef<AgGridReact>(null);

    const defaultColDef = useMemo(
        () => ({
            resizable: true,
            editable: true,
            sortable: true,
            filter: true,
            minWidth: 100,
            wrapText: true,
            autoHeight: true,
            cellStyle: {
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                lineHeight: '1.5em',
            },
        }),
        [],
    );

    const parseClipboardData = (clipboardData: string): ParseResult => {
        console.log('Raw clipboard data:', clipboardData);
        console.log('Clipboard data length:', clipboardData.length);
        console.log('Contains tabs:', clipboardData.includes('\t'));
        console.log('First 200 chars:', clipboardData.substring(0, 200));

        // Don't normalize line endings in advance - let the parser handle them
        // This preserves the original format for better parsing

        // Try to parse as TSV (tab-separated) first since that's what Excel/Numbers typically use
        if (clipboardData.includes('\t')) {
            console.log('Parsing as TSV...');
            const result = parseTSV(clipboardData);
            console.log('TSV parse result:', result);
            return result;
        }

        // Otherwise try CSV parsing
        console.log('Parsing as CSV...');
        return parseCSV(clipboardData);
    };

    const parseTSV = (tsvData: string): ParseResult => {
        // Handle Excel/Numbers TSV format which may have cells with embedded newlines
        const rows: string[][] = [];
        let currentRow: string[] = [];
        let currentCell = '';
        let i = 0;
        let inQuotes = false;

        while (i < tsvData.length) {
            const char = tsvData[i];
            const nextChar = i + 1 < tsvData.length ? tsvData[i + 1] : '';

            // Check if we're starting a quoted cell
            if (char === '"' && (currentCell === '' || inQuotes)) {
                if (inQuotes && nextChar === '"') {
                    // Escaped quote
                    currentCell += '"';
                    i += 2;
                    continue;
                } else if (
                    inQuotes &&
                    (nextChar === '\t' ||
                        nextChar === '\n' ||
                        nextChar === '\r' ||
                        i + 1 >= tsvData.length)
                ) {
                    // End of quoted cell
                    inQuotes = false;
                    i++;
                    continue;
                } else if (!inQuotes) {
                    // Start of quoted cell
                    inQuotes = true;
                    i++;
                    continue;
                }
            }

            // Handle delimiters when not in quotes
            if (!inQuotes) {
                if (char === '\t') {
                    currentRow.push(currentCell);
                    currentCell = '';
                    i++;
                    continue;
                } else if (char === '\n' || (char === '\r' && nextChar !== '\n')) {
                    currentRow.push(currentCell);
                    if (currentRow.length > 0) {
                        rows.push([...currentRow]);
                    }
                    currentRow = [];
                    currentCell = '';
                    i++;
                    continue;
                } else if (char === '\r' && nextChar === '\n') {
                    // Windows line ending
                    currentRow.push(currentCell);
                    if (currentRow.length > 0) {
                        rows.push([...currentRow]);
                    }
                    currentRow = [];
                    currentCell = '';
                    i += 2;
                    continue;
                }
            }

            currentCell += char;
            i++;
        }

        // Don't forget the last cell and row
        if (currentCell !== '' || currentRow.length > 0) {
            currentRow.push(currentCell);
        }
        if (currentRow.length > 0) {
            rows.push(currentRow);
        }

        if (rows.length === 0) return { data: [], columns: [], mapping: {} };

        const headers = rows[0];
        const dataRows = rows.slice(1);

        // Configure columns with better display settings for complex content
        const columns: ColDef[] = headers.map((header, i) => ({
            field: `col${i}`,
            headerName: header.trim() || `Column ${i + 1}`,
            editable: true,
            wrapText: true,
            autoHeight: true,
            width: 200,
            cellStyle: {
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                lineHeight: '1.5em',
                padding: '8px',
            },
        }));

        const mapping: Record<string, string> = {};
        headers.forEach((header, i) => {
            mapping[`col${i}`] = header.trim();
        });

        const data = dataRows.map((row, rowIndex) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const rowObj: any = { id: rowIndex };
            row.forEach((cell, colIndex) => {
                rowObj[`col${colIndex}`] = cell || '';
            });
            // Fill empty cells
            for (let i = row.length; i < headers.length; i++) {
                rowObj[`col${i}`] = '';
            }
            return rowObj;
        });

        return { data, columns, mapping };
    };

    const parseCSV = (csvData: string): ParseResult => {
        const rows: string[][] = [];
        let currentRow: string[] = [];
        let currentCell = '';
        let inQuotes = false;
        let i = 0;

        while (i < csvData.length) {
            const char = csvData[i];
            const nextChar = csvData[i + 1];

            if (char === '"') {
                if (inQuotes && nextChar === '"') {
                    // Escaped quote
                    currentCell += '"';
                    i += 2;
                    continue;
                } else {
                    // Toggle quote state
                    inQuotes = !inQuotes;
                    i++;
                    continue;
                }
            }

            if (!inQuotes) {
                if (char === ',') {
                    currentRow.push(currentCell);
                    currentCell = '';
                    i++;
                    continue;
                } else if (char === '\n') {
                    currentRow.push(currentCell);
                    if (currentRow.some((cell) => cell.trim())) {
                        rows.push(currentRow);
                    }
                    currentRow = [];
                    currentCell = '';
                    i++;
                    continue;
                }
            }

            currentCell += char;
            i++;
        }

        // Don't forget the last cell and row
        if (currentCell || currentRow.length > 0) {
            currentRow.push(currentCell);
            if (currentRow.some((cell) => cell.trim())) {
                rows.push(currentRow);
            }
        }

        if (rows.length === 0) return { data: [], columns: [], mapping: {} };

        // First row contains headers
        const headers = rows[0];
        const dataRows = rows.slice(1);

        // Create column definitions from headers
        const columns: ColDef[] = headers.map((header, i) => ({
            field: `col${i}`,
            headerName: header.trim() || `Column ${i + 1}`,
            editable: true,
        }));

        // Create column mapping (field -> headerName)
        const mapping: Record<string, string> = {};
        headers.forEach((header, i) => {
            mapping[`col${i}`] = header.trim();
        });

        // Create row data (skip the header row)
        const data = dataRows.map((row, rowIndex) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const rowObj: any = { id: rowIndex };
            row.forEach((cell, colIndex) => {
                rowObj[`col${colIndex}`] = cell;
            });
            // Fill empty cells
            for (let i = row.length; i < headers.length; i++) {
                rowObj[`col${i}`] = '';
            }
            return rowObj;
        });

        return { data, columns, mapping };
    };

    const handlePaste = useCallback(
        (event: ClipboardEvent) => {
            event.preventDefault();

            const clipboardData = event.clipboardData?.getData('text/plain');
            if (!clipboardData) return;

            const { data, columns, mapping } = parseClipboardData(clipboardData);

            if (data.length > 0 && columns.length > 0) {
                setColumnDefs(columns);
                setRowData(data);

                if (onDataChange) {
                    onDataChange(data, mapping || {});
                }
            }
        },
        [onDataChange],
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onGridReady = useCallback((params: any) => {
        // Focus the grid to enable paste
        params.api.setFocusedCell(0, 'col1');
    }, []);

    // Add paste listener on component mount
    React.useEffect(() => {
        const handlePasteEvent = (e: Event) => {
            // Check if the paste event is targeting the grid or its elements
            if (gridRef.current && e.target) {
                const targetElement = e.target as Element;
                // Check if the target is within the ag-grid themed container
                if (targetElement.closest('.ag-theme-quartz')) {
                    handlePaste(e as ClipboardEvent);
                }
            }
        };

        document.addEventListener('paste', handlePasteEvent);

        return () => {
            document.removeEventListener('paste', handlePasteEvent);
        };
    }, [handlePaste]);

    const clearGrid = () => {
        setRowData([]);
        setColumnDefs([
            { field: 'col1', headerName: 'Column 1', editable: true },
            { field: 'col2', headerName: 'Column 2', editable: true },
            { field: 'col3', headerName: 'Column 3', editable: true },
        ]);
    };

    return (
        <div className="h-full flex flex-col">
            <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Paste Data Grid
                </h3>
                <p className="text-sm text-gray-600 mb-2">
                    Copy data from Excel or CSV (including headers), then:
                </p>
                <ol className="text-sm text-gray-600 mb-4 list-decimal list-inside">
                    <li>Click anywhere in the grid below</li>
                    <li>Press Ctrl+V (Windows/Linux) or Cmd+V (Mac) to paste</li>
                    <li>First row will be used as column names</li>
                </ol>
                <div className="flex gap-2 items-center">
                    <button
                        onClick={clearGrid}
                        className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                    >
                        Clear Grid
                    </button>
                    <textarea
                        placeholder="Or paste directly here..."
                        className="flex-1 px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        rows={1}
                        onPaste={(e) => {
                            e.preventDefault();
                            const clipboardData = e.clipboardData?.getData('text/plain');
                            if (clipboardData) {
                                const { data, columns, mapping } =
                                    parseClipboardData(clipboardData);
                                if (data.length > 0 && columns.length > 0) {
                                    setColumnDefs(columns);
                                    setRowData(data);
                                    if (onDataChange) {
                                        onDataChange(data, mapping || {});
                                    }
                                }
                                // Clear the textarea
                                e.currentTarget.value = '';
                            }
                        }}
                    />
                </div>
            </div>
            <div
                className="ag-theme-quartz flex-1 border border-gray-200 rounded-md focus-within:ring-2 focus-within:ring-indigo-500"
                style={{ height: '400px', minHeight: '400px' }}
                onPaste={(e) => {
                    e.preventDefault();
                    const clipboardData = e.clipboardData?.getData('text/plain');
                    if (clipboardData) {
                        const { data, columns, mapping } =
                            parseClipboardData(clipboardData);
                        if (data.length > 0 && columns.length > 0) {
                            setColumnDefs(columns);
                            setRowData(data);
                            if (onDataChange) {
                                onDataChange(data, mapping || {});
                            }
                        }
                    }
                }}
            >
                <AgGridReact
                    ref={gridRef}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    onGridReady={onGridReady}
                    animateRows={true}
                    suppressCopyRowsToClipboard={false}
                    enableCellTextSelection={true}
                    ensureDomOrder={true}
                />
            </div>
            {rowData.length > 0 && (
                <div className="mt-2 text-sm text-gray-600">
                    Rows: {rowData.length} | Columns: {columnDefs.length}
                </div>
            )}
        </div>
    );
};

export default PasteGrid;
